import { useQuery } from '@tanstack/react-query';
import { TrendingUp, Wallet } from 'lucide-react';
import { request } from '../api/client';
import { inr, inr2 } from '../lib/format';

/* ---------------------------------------------------------------------------
 * WHAT THE DEALER IS EARNING — shown where it can change what they order.
 *
 * The terms page lists a dealer's slabs. It does not tell them where they STAND in those slabs, and
 * a page they have to go and open does not change anyone's order. These two surfaces put the same
 * figures at the two moments that matter: on the dashboard when they log in, and in the cart while
 * they are still deciding what to buy.
 *
 * Every number comes from `GET /discount-schemes/my-earnings`, which computes turnover with the same
 * engine the year-end payout uses. Nothing here is estimated in the browser except the prepay slab
 * pick, which is a plain threshold (see `advanceCdPct`), and the definitive figure is still the
 * server's own preview shown at Place Order.
 * ------------------------------------------------------------------------- */

export interface MyEarnings {
  scheme: { id: string; name: string; financialYear: string; creditPeriodDays: number };
  turnover: { financialYear: string; from: string; to: string; gross: number; returns: number; net: number };
  tod: {
    pct: number;
    band: { fromAmount: number; toAmount: number | null; basePct: number; perLakhPct: number; capPct: number } | null;
    atCap: boolean;
    nextPct: number | null;
    nextAt: number | null;
    gapToNext: number | null;
  } | null;
  advanceCd: { slabs: { minAmount: number; cdPct: number }[]; bestPct: number };
  countsWhenFulfilled: boolean;
}

/**
 * Only a dealer has terms of their own; the endpoint returns null for a staff login, so this stays
 * quiet everywhere else rather than needing a role check at each call site.
 */
export function useMyEarnings() {
  return useQuery({
    queryKey: ['my-earnings'],
    queryFn: () => request<MyEarnings | null>({ url: '/discount-schemes/my-earnings' }),
    staleTime: 5 * 60_000,
  });
}

/**
 * The prepay discount an order of `value` would earn: the highest slab whose threshold it clears.
 *
 * Mirrors `pickAdvanceSlab` on the server. It is a plain "highest minAmount ≤ amount" lookup rather
 * than a calculation, and the authoritative figure is still the server's estimate in the Place Order
 * prompt — this only has to be right enough to be worth acting on before you get there.
 */
export function advanceCdPct(slabs: { minAmount: number; cdPct: number }[], value: number): { pct: number; slab: { minAmount: number; cdPct: number } | null; next: { minAmount: number; cdPct: number } | null } {
  const sorted = [...(slabs ?? [])].sort((a, b) => a.minAmount - b.minAmount);
  const slab = [...sorted].reverse().find((s) => value >= s.minAmount) ?? null;
  const next = sorted.find((s) => s.minAmount > value) ?? null;
  return { pct: slab?.cdPct ?? 0, slab, next };
}

/** Turnover only counts once an order is actually fulfilled — never let a screen imply otherwise. */
const FULFILMENT_NOTE = 'Counts once the order is delivered.';

/**
 * Dashboard strip: where the dealer stands this year, and the next step.
 *
 * Renders nothing when the dealer is on no live scheme — an empty progress bar promising a discount
 * nobody has assigned them would be worse than silence.
 */
export function EarningStrip() {
  const { data } = useMyEarnings();
  if (!data?.tod) return null;
  const { turnover, tod, scheme } = data;

  // Progress within the current band, so the bar means something rather than being decorative.
  const bandFrom = tod.band?.fromAmount ?? 0;
  const target = tod.nextAt ?? turnover.net;
  const span = Math.max(1, target - bandFrom);
  const pctFilled = Math.max(0, Math.min(100, ((turnover.net - bandFrom) / span) * 100));

  return (
    <section aria-label="Your turnover discount" className="rounded-card border border-line bg-surface shadow-e1 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-2 type-body-strong text-content">
          <TrendingUp className="size-4 text-brand-600" />
          Your turnover discount
        </span>
        <span className="type-caption text-subtle">{scheme.name} · FY {turnover.financialYear}</span>
      </div>

      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="type-metric text-content">{inr(turnover.net)}</span>
        <span className="type-body text-muted">purchased this year, earning</span>
        <span className="type-metric-sm text-brand-700 dark:text-brand-400">{tod.pct}%</span>
      </div>

      <div className="mt-2.5 h-2 overflow-hidden rounded-pill bg-surface-3">
        <div className="h-full rounded-pill bg-brand-500 transition-[width] duration-500" style={{ width: `${pctFilled}%` }} />
      </div>

      <div className="mt-2 type-body text-muted">
        {tod.gapToNext != null && tod.nextPct != null ? (
          <>
            <span className="font-medium text-content">{inr2(tod.gapToNext)} more</span> takes you to{' '}
            <span className="font-medium text-brand-700 dark:text-brand-400">{tod.nextPct}%</span>.{' '}
            <span className="text-subtle">{FULFILMENT_NOTE}</span>
          </>
        ) : (
          // At the cap there is nothing further to earn — say so instead of inventing a next step.
          <span>You are at the highest rate your band offers ({tod.band?.capPct ?? tod.pct}%).</span>
        )}
      </div>
    </section>
  );
}

/**
 * Cart panel: what THIS order is worth, while the dealer can still change it.
 *
 * Two different levers, and they are not interchangeable. The prepay discount comes off this
 * invoice; the turnover step is a credit note after year end. Saying which is which is the
 * difference between a useful prompt and a disappointed dealer in April.
 */
export function CartEarningPanel({ cartValue }: { cartValue: number }) {
  const { data } = useMyEarnings();
  if (!data) return null;

  const cd = advanceCdPct(data.advanceCd.slabs, cartValue);
  const tod = data.tod;
  // Where this cart would leave them if it ships — the honest version of "this order gets you there".
  const wouldReach = tod?.gapToNext != null && cartValue >= tod.gapToNext;
  if (!cd.pct && !cd.next && !tod) return null;

  return (
    <section aria-label="Your scheme" className="mt-3 rounded-control border border-brand-500/25 bg-brand-500/6 p-3">
      <div className="inline-flex items-center gap-1.5 type-label text-brand-700 dark:text-brand-300">
        <Wallet className="size-3.5" />
        Your scheme
      </div>

      {/* §1 Prepay — comes straight off this order, so it leads. */}
      {cd.pct > 0 ? (
        <div className="mt-1.5 type-body text-content">
          Prepay this order and it is <span className="font-semibold">{cd.pct}% off</span> —{' '}
          <span className="font-semibold text-brand-700 dark:text-brand-400">{inr2((cartValue * cd.pct) / 100)}</span> off now.
        </div>
      ) : cd.next ? (
        <div className="mt-1.5 type-body text-muted">
          Add <span className="font-medium text-content">{inr2(cd.next.minAmount - cartValue)}</span> and prepaying earns{' '}
          <span className="font-medium text-brand-700 dark:text-brand-400">{cd.next.cdPct}% off</span> this order.
        </div>
      ) : null}

      {/* §4 Turnover — a credit note after year end, and only on fulfilled orders. Both facts stated. */}
      {tod?.gapToNext != null && tod.nextPct != null && (
        <div className="mt-1.5 border-t border-brand-500/15 pt-1.5 type-caption text-muted">
          {wouldReach ? (
            <>Once delivered, this order takes your year-end turnover discount to{' '}
              <span className="font-medium text-content">{tod.nextPct}%</span>.</>
          ) : (
            <>{inr2(tod.gapToNext)} more this year takes your turnover discount from {tod.pct}% to{' '}
              <span className="font-medium text-content">{tod.nextPct}%</span>. {FULFILMENT_NOTE}</>
          )}
        </div>
      )}
    </section>
  );
}
